import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { START_TOUR_EVENT, TOUR_STEPS, TOUR_STORAGE_KEY } from './consts';
import {
  StyledTourBackdrop,
  StyledTourBadge,
  StyledTourBadgeRow,
  StyledTourBody,
  StyledTourButton,
  StyledTourClickCatcher,
  StyledTourCount,
  StyledTourFooter,
  StyledTourHighlight,
  StyledTourPopover,
  StyledTourSkip,
  StyledTourTitle,
} from './styles';

const TARGET_POLL_MS = 150;
const TARGET_TIMEOUT_MS = 4000;
const AUTO_START_DELAY_MS = 1400;
const POPOVER_HEIGHT_GUESS = 230;

type Rect = { top: number; left: number; width: number; height: number };

// The layout (and this component with it) remounts on every route change,
// and the tour itself navigates between routes — so its progress must live
// outside React state to survive the remount and resume where it was.
const tourSession = { active: false, stepIndex: 0 };

const FeatureTour: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  const pollRef = useRef<number>();

  const step = TOUR_STEPS[stepIndex];

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = undefined;
    }
  };

  const dismiss = useCallback(() => {
    window.localStorage.setItem(TOUR_STORAGE_KEY, 'seen');
    tourSession.active = false;
    tourSession.stepIndex = 0;
    stopPolling();
    setActive(false);
    setReady(false);
    setRect(null);
  }, []);

  const measure = (element: Element): Rect => {
    const r = element.getBoundingClientRect();
    return { top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12 };
  };

  const goToStep = useCallback(
    (index: number) => {
      if (index >= TOUR_STEPS.length) {
        dismiss();
        return;
      }

      const next = TOUR_STEPS[index];
      tourSession.stepIndex = index;
      setStepIndex(index);
      setReady(false);
      setRect(null);
      stopPolling();

      if (window.location.pathname !== next.route) {
        history.push(next.route);
      }

      if (!next.target) {
        setReady(true);
        return;
      }

      const startedAt = Date.now();
      pollRef.current = window.setInterval(() => {
        const el = document.querySelector(`[data-tour="${next.target}"]`);

        if (el) {
          stopPolling();
          el.scrollIntoView({ block: 'center', behavior: 'auto' });
          window.requestAnimationFrame(() => {
            setRect(measure(el));
            setReady(true);
          });
          return;
        }

        if (Date.now() - startedAt > TARGET_TIMEOUT_MS) {
          stopPolling();
          // Target never appeared (e.g. no data) — skip past it.
          goToStep(index + 1);
        }
      }, TARGET_POLL_MS);
    },
    [dismiss, history]
  );

  const start = useCallback(() => {
    tourSession.active = true;
    setActive(true);
    goToStep(0);
  }, [goToStep]);

  // On mount: resume a tour that was mid-flight across a route change,
  // otherwise auto-run once on the first visit after the release.
  useEffect(() => {
    if (tourSession.active) {
      setActive(true);
      goToStep(tourSession.stepIndex);
      return undefined;
    }

    if (window.localStorage.getItem(TOUR_STORAGE_KEY)) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (
        !window.localStorage.getItem(TOUR_STORAGE_KEY) &&
        !tourSession.active
      ) {
        start();
      }
    }, AUTO_START_DELAY_MS);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The header's ? icon (or anything else) can relaunch the tour.
  useEffect(() => {
    const onStart = () => start();
    window.addEventListener(START_TOUR_EVENT, onStart);
    return () => window.removeEventListener(START_TOUR_EVENT, onStart);
  }, [start]);

  // Keep the spotlight glued to the target on resize.
  useEffect(() => {
    if (!active || !step?.target) {
      return undefined;
    }

    const onResize = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el) {
        setRect(measure(el));
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active, step]);

  useEffect(() => stopPolling, []);

  if (!active || !ready || !step) {
    return null;
  }

  const isLast = stepIndex === TOUR_STEPS.length - 1;
  const centered = !step.target || !rect;

  const popoverStyle: React.CSSProperties = centered
    ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    : (() => {
        const below = rect!.top + rect!.height + POPOVER_HEIGHT_GUESS < window.innerHeight;
        const left = Math.min(
          Math.max(rect!.left + rect!.width / 2 - 170, 16),
          window.innerWidth - 356
        );
        return below
          ? { top: rect!.top + rect!.height + 12, left }
          : { top: rect!.top - 12, left, transform: 'translateY(-100%)' };
      })();

  return createPortal(
    <div role='dialog' aria-modal='true' aria-label='Feature tour'>
      <StyledTourClickCatcher onClick={(e) => e.stopPropagation()} />
      {centered ? (
        <StyledTourBackdrop />
      ) : (
        <StyledTourHighlight
          style={{
            top: rect!.top,
            left: rect!.left,
            width: rect!.width,
            height: rect!.height,
          }}
        />
      )}
      <StyledTourPopover style={popoverStyle} data-tour-root>
        <StyledTourBadgeRow>
          <StyledTourBadge>
            {intl.formatMessage({ id: 'tour.badge', defaultMessage: "What's new" })}
          </StyledTourBadge>
          <StyledTourCount>
            {intl.formatMessage(
              { id: 'tour.stepCount', defaultMessage: '{current} of {total}' },
              { current: stepIndex + 1, total: TOUR_STEPS.length }
            )}
          </StyledTourCount>
        </StyledTourBadgeRow>
        <StyledTourTitle>
          {intl.formatMessage({ id: step.titleId, defaultMessage: step.defaultTitle })}
        </StyledTourTitle>
        <StyledTourBody>
          {intl.formatMessage({ id: step.bodyId, defaultMessage: step.defaultBody })}
        </StyledTourBody>
        <StyledTourFooter>
          {!isLast && (
            <StyledTourSkip type='button' onClick={dismiss}>
              {intl.formatMessage({ id: 'tour.skip', defaultMessage: 'Skip tour' })}
            </StyledTourSkip>
          )}
          {stepIndex > 0 && (
            <StyledTourButton type='button' onClick={() => goToStep(stepIndex - 1)}>
              {intl.formatMessage({ id: 'forms.buttons.back', defaultMessage: 'Back' })}
            </StyledTourButton>
          )}
          <StyledTourButton primary type='button' onClick={() => goToStep(stepIndex + 1)}>
            {isLast
              ? intl.formatMessage({ id: 'tour.done', defaultMessage: 'Done' })
              : intl.formatMessage({ id: 'forms.buttons.next', defaultMessage: 'Next' })}
          </StyledTourButton>
        </StyledTourFooter>
      </StyledTourPopover>
    </div>,
    document.body
  );
};

export default FeatureTour;
